import { closestCenter, DndContext, DragEndEvent } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import FunctionsIcon from '@mui/icons-material/Functions';
import SaveIcon from '@mui/icons-material/Save';
import { Button, styled, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { OrderStatus } from 'harmony-constants';
import { L } from 'harmony-language';
import React from 'react';
import { OrgQueryKeys } from '../../../../api/config';
import { useAddTravelTimes } from '../../../../api/mutations/reworked/add/use-add-travel-times';
import { useDraftsMutation } from '../../../../api/mutations/reworked/use-drafts-mutation';
import { useOrganizationQuery } from '../../../../api/queries/use-organization-query';
import { useTractorTrailer } from '../../../../api/queries/use-tractor-trailer';
import { useUser } from '../../../../api/queries/use-user';
import type { Driver, Load, LocationGroup, OrganizationLocation, Stop } from '../../../../types';
import { stopsToOrganizationLocationIdPairs } from '../../../../utils/data-mapping';
import { toDriverDisplayName, travelTimesCacheKey } from '../../../../utils/data-mapping-utils';
import { add, getDuration, subtract, utc } from '../../../../utils/date-time-utils';
import { withThroughputs } from '../../../../utils/throughput-utils';
import { AgisticsSelect, createAgisticsSelectElements } from '../../../shared/agistics-select';
import { CircularLoading } from '../../../shared/circular-loading';
import { AgisticsDateTimePicker } from '../../../shared/inputs/agistics-date-time-picker';
import { findUnknownPairsInCache, nonHookNonLoadTravelTime, TravelTimesCache, updatePointlessPairs } from '../../../travel-times/use-travel-times';
import { BulkResourceSchedulingButton } from '../scheduling/resource-scheduling-button';
import { BulkScheduleDraft } from './bulk-schedule-draft';
import { BulkScheduleMap } from './bulk-schedule-map';
import { BulkScheduleRouteFigures } from './bulk-schedule-route-figures';
import { AgisticsColors } from '../../../../styles/agistics-colors';
import { LoadStopLocation } from '../../drafts-loads-table-cells/loads/display/load-stop-location';

interface BulkScheduleProps {
    drafts: Load[];
    onSubmit: () => void;
}

export const BulkSchedule = (props: BulkScheduleProps) => {
    const { drafts, onSubmit } = props;
    const { organizationId } = useUser();
    const { mutate: saveDrafts } = useDraftsMutation();
    const { data: drivers = [], isLoading: isLoadingDrivers } = useOrganizationQuery<Driver[]>(OrgQueryKeys.drivers);
    const { data: locations = [] } = useOrganizationQuery<OrganizationLocation[]>(OrgQueryKeys.locations);
    const { tractors = [], trailers = [], isLoadingTractors, isLoadingTrailers } = useTractorTrailer();
    const { mutate: addTravelTimes } = useAddTravelTimes(organizationId);
    const queryKey = OrgQueryKeys.resolve(organizationId, OrgQueryKeys.locationTravelTimes);
    const queryClient = useQueryClient();
    const travelTimesCache = queryClient.getQueryData<TravelTimesCache>(([queryKey]));
    const { data: throughputs } = useOrganizationQuery(OrgQueryKeys.throughput);

    const [modifiedDrafts, setModifiedDrafts] = React.useState<Load[]>(drafts);
    const [isUsingFirstStop, setIsUsingFirstStop] = React.useState<boolean>(true);
    const [referenceTime, setReferenceTime] = React.useState<string | null>(drafts[0].stops[0].arrivalTime);
    const isSameDriver = modifiedDrafts.every((draft) => draft.transportedByUserId === modifiedDrafts[0].transportedByUserId);
    const [assignedDriverId, setAssignedDriver] = React.useState<number>(isSameDriver ? (modifiedDrafts[0].transportedByUserId || -1) : -1);
    const [assignedTractorId, setAssignedTractor] = React.useState<number>(-1);
    const [assignedTrailerId, setAssignedTrailer] = React.useState<number>(-1);
    const [isDragging, setIsDragging] = React.useState<boolean>(false);
    const [hoveredDraftId, setHoveredDraftId] = React.useState<number | null>(null);
    const [homeStartTravelTime, setHomeStartTravelTime] = React.useState<string>('');
    const [homeEndTravelTime, setHomeEndTravelTime] = React.useState<string>('');

    
    const isLoading = isLoadingDrivers || isLoadingTractors || isLoadingTrailers;
    const homeLocationId = drivers.find((driver) => driver.id === assignedDriverId)?.driverInfo?.homeLocationId;
    const homeLocation = locations.find((loc) => loc.id === homeLocationId);
    const hasNoDriver = assignedDriverId < 1;
    const disableActionButtons = !referenceTime || hasNoDriver;
    const stopsForResourceScheduling = drafts.flatMap((draft) => draft.stops);
    const driverSelectElements = React.useMemo(() =>
        createAgisticsSelectElements(drivers, (driver) => driver.name)
    , [drivers]);
    const tractorSelectElements = React.useMemo(() =>
        createAgisticsSelectElements(tractors, (tractor) => tractor.userDisplayName)
    , [tractors]);
    const trailerSelectElements = React.useMemo(() =>
        createAgisticsSelectElements(trailers, (trailer) => trailer.userDisplayName)
    , [trailers]);

    React.useEffect(() => {
        if (!homeLocationId || isDragging) {
            setHomeStartTravelTime('');
            setHomeEndTravelTime('');
            return;
        }

        const firstDraftLocationId = modifiedDrafts[0].stops[0].organizationLocationId;
        const lastDraftLocationId = modifiedDrafts.at(-1)!.stops.at(-1)!.organizationLocationId;
        const firstTravelTimePairKey = travelTimesCacheKey([homeLocationId, firstDraftLocationId]);
        const lastTravelTimePairKey = travelTimesCacheKey([lastDraftLocationId, homeLocationId]);

        if (travelTimesCache && !travelTimesCache[firstTravelTimePairKey]) {
            addTravelTimes([[homeLocationId, firstDraftLocationId]]);
        } else if (travelTimesCache && !travelTimesCache[lastTravelTimePairKey]) {
            addTravelTimes([[lastDraftLocationId, homeLocationId]]);
        }

        const startHomeTravelTimeMinutes = nonHookNonLoadTravelTime(travelTimesCache, [firstTravelTimePairKey]);
        setHomeStartTravelTime(getDuration(startHomeTravelTimeMinutes * 60000));
        const endHomeTravelTimeMinutes = nonHookNonLoadTravelTime(travelTimesCache, [lastTravelTimePairKey]);
        setHomeEndTravelTime(getDuration(endHomeTravelTimeMinutes * 60000));
    }, [modifiedDrafts, homeLocationId, isDragging, travelTimesCache]);

    function handleDragEnd(event: DragEndEvent) {
        const {active, over} = event;
    
        if (over && active.id !== over.id) {
            const oldIndex = modifiedDrafts.findIndex((draft) => draft.id === active.id);
            const newIndex = modifiedDrafts.findIndex((draft) => draft.id === over.id);
            const updatedArray = arrayMove(modifiedDrafts, oldIndex, newIndex);            
            setModifiedDrafts(updatedArray);

            // This section will update travel times from draft to draft after they're reordered
            const updatedStops = updatedArray.flatMap(draft => draft.stops);
            if (travelTimesCache) {
                const pairs = stopsToOrganizationLocationIdPairs(updatedStops, false);
                const unknowPairs = findUnknownPairsInCache(travelTimesCache, pairs);
                const filteredUnknowPairs = updatePointlessPairs(unknowPairs, travelTimesCache, queryClient, queryKey);
    
                if (filteredUnknowPairs.length) {
                    addTravelTimes(unknowPairs);
                }
            }
        }
        setIsDragging(false);
    }

    function getTravelTime(index: number) {
        if (isDragging) return '';
        
        const prevLocationId = modifiedDrafts[index - 1].stops.slice(-1)[0].organizationLocationId;
        const nextLocationId = modifiedDrafts[index].stops[0].organizationLocationId;
        const locationPairKey = travelTimesCacheKey([prevLocationId, nextLocationId]);
        const travelTimeMinutes = nonHookNonLoadTravelTime(travelTimesCache, [locationPairKey]);
        return getDuration(travelTimeMinutes * 60000);
    }

    function calculateUsingFirstStop() {
        let prevStop: Stop | null = null;
        const updatedDrafts = modifiedDrafts.map((draft) => {
            const stopsWithThroughput = draft.stops.map(withThroughputs(throughputs)) as Stop[];

            const updatedStops = stopsWithThroughput.map((stop) => {
                if (!prevStop) {
                    const updatedStop = {
                        ...stop,
                        arrivalTime: referenceTime,
                    };

                    prevStop = updatedStop;
                    return updatedStop;
                }

                const legTimeSeconds = travelTimesCache?.[travelTimesCacheKey([prevStop.organizationLocationId, stop.organizationLocationId])]?.timeSeconds || 0;
                const minutesToAdd = (legTimeSeconds / 60) + (prevStop.durationMinutes || 0);

                const updatedStop = {
                    ...stop,
                    arrivalTime: utc(add(minutesToAdd, 'minutes', prevStop.arrivalTime)),
                }

                prevStop = updatedStop;
                return updatedStop;
            });

            return {
                ...draft,
                ...(updatedStops[0].arrivalTime && { date: updatedStops[0].arrivalTime }),
                stops: updatedStops
            }
        });
        
        setModifiedDrafts([...updatedDrafts]);
    }

    function calculateUsingLastStop() {
        let stopFollowingCurrent: Stop | null = null;
        const resultDrafts = [];
        
        for (let i = modifiedDrafts.length - 1; i >= 0; i--) {
            const draft = modifiedDrafts[i];
            const stopsWithThroughput = draft.stops.map(withThroughputs(throughputs)) as Stop[];
            const resultStops = [];
            
            for (let j = stopsWithThroughput.length - 1; j >= 0; j--) {
                const currentWorkingStop = stopsWithThroughput[j];
                if (!stopFollowingCurrent) {
                    const resultStop = {
                        ...currentWorkingStop,
                        arrivalTime: referenceTime
                    } as Stop;
                    resultStops.push(resultStop);
                    stopFollowingCurrent = resultStop;
                    continue;
                }

                const legTimeSeconds = travelTimesCache?.[travelTimesCacheKey([currentWorkingStop.organizationLocationId, stopFollowingCurrent.organizationLocationId])]?.timeSeconds || 0;
                const minutesToSubtract = (legTimeSeconds / 60) + (currentWorkingStop.durationMinutes || 0);

                const resultStop = {
                    ...currentWorkingStop,
                    arrivalTime: utc(subtract(minutesToSubtract, 'minutes', stopFollowingCurrent.arrivalTime))
                } as Stop;
                resultStops.unshift(resultStop);
                stopFollowingCurrent = resultStop;
            }

            resultDrafts.unshift({
                ...draft,
                ...(resultStops[0].arrivalTime && { date: resultStops[0].arrivalTime }),
                stops: resultStops
            } as Load);
        }

        setModifiedDrafts(resultDrafts);
    }

    function handleAssignDriver(selectedId: number) {
        const defaultTractorId = drivers.find((driver) => driver.id === selectedId)?.driverInfo?.defaultTractorId || -1;
        const defaultTrailerId = drivers.find((driver) => driver.id === selectedId)?.driverInfo?.defaultTrailerId || -1;
        
        setAssignedDriver(selectedId);
        setAssignedTractor(defaultTractorId);
        setAssignedTrailer(defaultTrailerId);

        setModifiedDrafts((prevDrafts) => 
            prevDrafts.map((draft) => ({
                ...draft,
                status: selectedId !== -1 ? OrderStatus.Assigned : OrderStatus.Open,
                transportedByUserId: selectedId,
                tractorId: defaultTractorId > 0 ? defaultTractorId : draft.tractorId,
                trailerId: defaultTrailerId > 0 ? defaultTrailerId : draft.trailerId,
            }))
        );
    }

    function handleAssignTrailer(selectedId: number) {
        setAssignedTrailer(selectedId);
        setModifiedDrafts((prevDrafts) =>
            prevDrafts.map((draft) => ({
                ...draft,
                trailerId: selectedId,
            }))
        );
    }

    function handleAssignTractor(selectedId: number) {
        setAssignedTractor(selectedId);
        setModifiedDrafts((prevDrafts) =>
            prevDrafts.map((draft) => ({
                ...draft,
                tractorId: selectedId,
            }))
        );
    }

    function handleSubmit() {
        saveDrafts({ drafts: modifiedDrafts });
        onSubmit();
    }

    if (isLoading) return <div style={{ width: '100vw', height: '60vh' }}><CircularLoading /></div>;

    return (
        <ContentContainer>
            <LeftContainer>
                <BulkScheduleDraftColumn style={{ backgroundColor: AgisticsColors.lightGray }}>
                    <div/>
                    <DraftHeader>{L.date()}</DraftHeader>
                    <DraftHeader>{L.orderNumber()}</DraftHeader>
                    <DraftHeader>{L.cargo()}</DraftHeader>
                    <DraftHeader>{L.origin()}</DraftHeader>
                    <DraftHeader>{L.destination()}</DraftHeader>
                    <DraftHeader style={{ textAlign: 'center' }}>{L.estimatedTotalTripTime()}</DraftHeader>
                    <DraftHeader>{L.driver()}</DraftHeader>
                    <div/>
                </BulkScheduleDraftColumn>

                <DndContext
                    onDragStart={() => setIsDragging(true)}
                    onDragEnd={handleDragEnd}
                    modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                    collisionDetection={closestCenter}
                >
                    <SortableContext
                        items={modifiedDrafts.map((draft) => draft.id)}
                        strategy={verticalListSortingStrategy}
                    >
                        <DriverHomeLocationRow homeLocation={homeLocation} organizationLocations={locations} travelTime={homeStartTravelTime} />
                        {modifiedDrafts.map((draft, i) =>
                            <>
                                {i !== 0 && <TravelTimeRow>{`${L.travelTime()}: ${getTravelTime(i)}`}</TravelTimeRow>}
                                <BulkScheduleDraft key={draft.id}
                                    draft={draft}
                                    hoveredDraftIdState={{ hoveredDraftId, setHoveredDraftId }}
                                    onUpdateStops={(updatedStops) => {
                                        setModifiedDrafts((prevDrafts) => 
                                            prevDrafts.map((prevDraft) => 
                                                prevDraft.id === draft.id ? {...prevDraft, stops: updatedStops} : prevDraft
                                            )
                                        );
                                    }}
                                    driverName={toDriverDisplayName(drivers)(draft)}
                                />
                            </>
                        )}
                        <DriverHomeLocationRow homeLocation={homeLocation} organizationLocations={locations} travelTime={homeEndTravelTime} />
                    </SortableContext>
                </DndContext>
            </LeftContainer>
            
            <RightContainer>
                <BulkScheduleMap drafts={modifiedDrafts} hoveredDraftIdState={{ hoveredDraftId, setHoveredDraftId}} />
                <SettingsContainer>
                    <SettingsLeftContainer>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <BulkScheduleRouteFigures drafts={modifiedDrafts} travelTimesCache={travelTimesCache} driverHomeLocationId={homeLocation?.id} />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant='caption'>{`${L.scheduleUsing()}:`}</Typography>
                            <ToggleButtonGroup
                                value={isUsingFirstStop}
                                exclusive
                                color='primary'
                                onChange={(e, v) => setIsUsingFirstStop(v)}
                            >
                                <ToggleButton value={true}>{L.firstStop()}</ToggleButton>
                                <ToggleButton value={false}>{L.lastStop()}</ToggleButton>
                            </ToggleButtonGroup>
                        </div>
                        <div style={{ maxWidth: '12rem' }}>
                            <AgisticsDateTimePicker
                                label={`${L.referenceTime()}:`}
                                value={referenceTime}
                                onChange={setReferenceTime}
                            />
                        </div>
                        <Tooltip title={disableActionButtons && L.bulkScheduleDisabledActionTooltip()}>
                            <span>
                                <Button
                                    startIcon={<FunctionsIcon />}
                                    color='primary'
                                    variant='contained'
                                    disabled={disableActionButtons}
                                    onClick={isUsingFirstStop ? calculateUsingFirstStop : calculateUsingLastStop}
                                    >
                                    {L.calculateSchedule()}
                                </Button>
                            </span>
                        </Tooltip>
                    </SettingsLeftContainer>
                        
                    <SettingsRightContainer>
                        <BulkResourceSchedulingButton stops={stopsForResourceScheduling} />
                        <AgisticsSelect
                            label={L.driver()}
                            item={assignedDriverId}
                            items={driverSelectElements}
                            showNone
                            onChange={handleAssignDriver}
                        />
                        <AgisticsSelect
                            label={L.tractor()}
                            item={assignedTractorId}
                            items={tractorSelectElements}
                            showNone
                            onChange={handleAssignTractor}
                        />
                        <AgisticsSelect
                            label={L.trailer()}
                            item={assignedTrailerId}
                            items={trailerSelectElements}
                            showNone
                            onChange={handleAssignTrailer}
                        />
                    </SettingsRightContainer>
                </SettingsContainer>

                <SaveContainer>
                    <Tooltip title={disableActionButtons && L.bulkScheduleDisabledActionTooltip()}>
                        <span>
                            <Button
                                startIcon={<SaveIcon />}
                                color='primary'
                                variant='contained'
                                disabled={disableActionButtons}
                                onClick={handleSubmit}
                                >
                                {L.save()}
                            </Button>
                        </span>
                    </Tooltip>
                </SaveContainer>

            </RightContainer>
        </ContentContainer>
    );
}

interface DriverHomeLocationRowProps {
    homeLocation: OrganizationLocation | undefined;
    organizationLocations: OrganizationLocation[];
    travelTime: string;
}

const DriverHomeLocationRow = (props: DriverHomeLocationRowProps) => {
    const { homeLocation, organizationLocations, travelTime } = props;
    const { data: locationGroups = [] } = useOrganizationQuery<LocationGroup[]>(OrgQueryKeys.locationGroups);

    if (!homeLocation) return null;
    return (
        <TravelTimeRow>
            <span>{`${L.driverHomeLocationTravelTime()}: ${travelTime}`}</span>
            <LoadStopLocation
                organizationLocationId={homeLocation.id}
                organizationLocations={organizationLocations}
                locationGroups={locationGroups}
                stops={[]}
                includeStopNotes={false}
            />
        </TravelTimeRow>
    );
}

const ContentContainer = styled('div')({
    display: 'flex',
    width: '100vw',
    fontSize: '.875rem',
    gap: '1rem',
    margin: '1rem',
});

const LeftContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    width: '60%',
    padding: '0.5rem',
});

export const BulkScheduleDraftColumn = styled('div')({
    display: 'grid',
    gap: '0.5rem',
    gridTemplateColumns: ' 0.2fr 1fr 0.5fr 1fr 1fr 1fr 0.8fr 1fr 0.2fr',
    padding: '1rem',
});

const TravelTimeRow = styled('div')({
    padding: '0.5rem',
    backgroundColor: AgisticsColors.lightGray2,
});

const DraftHeader = styled('div')({
    fontWeight: 'bold',
});

const RightContainer = styled('div')({
    width: '40%',
    padding: '0.5rem',
});

const SettingsContainer = styled('div')({
    display: 'flex',
    gap: '1rem'
});

const SettingsLeftContainer = styled('div')({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
});

const SettingsRightContainer = styled('div')({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    justifyContent: 'flex-end',
});

const SaveContainer = styled('div')({
    display: 'flex',
    marginTop: '2rem',
    justifyContent: 'flex-end'
});